import React, { useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { Grid, makeStyles, Box, Menu, Icon, MenuItem, ListItemIcon, Checkbox, Divider, IconButton, Radio, RadioGroup, Tooltip, Theme,
    Button, Paper, TextField, FormControl, InputLabel, Select, alpha } from '@material-ui/core';
import clsx from 'clsx';
import produce, { castDraft, Draft } from 'immer';
import { useDebounce } from 'use-debounce';
import { DialogResponse, DialogResponseCode, DialogType, modeUIContext, selectLanguage, showDialog } from '@moderepo/mode-ui-state';
import { Agent, NumericMetricInfo, TagMetricInfo, TimeSeriesResolution } from '@moderepo/mode-internal-apis';
import { ritzDataContext, selectLoggedInAgent, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { useModePanelStyle, getLanguageByCode, useSearchParams, useDropdown, capitalizeAll, capitalizeFirst,
    useRebuildMetricsPerChartArray, MultiSeriesDataPoint, ChartSettings, TimeSeriesDataTable, PaginationBar, ITEMS_PER_PAGE_LIST, PAGING_MIN_PAGES,
    TimeSeriesDataTableProps, ITEMS_PER_PAGE_20, TimeSeriesChartPanel, XYChart, XYChartProps, Time, XYChartType, resolutionToMilliseconds, FontIcon,
    GenericMessageBox, TimeSeriesTableCompField } from '@moderepo/mode-app-components';
import { TFunction } from 'i18next';
import { KeyboardDateTimePicker } from '@material-ui/pickers';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import moment from 'moment/moment';
import { useRitzTranslation } from '../../utils/customHooks';
import * as Utils from '../../utils/utils';
import * as Constants from '../../utils/constants';
import { GanttChart } from './GanttChart';
import { ChartSize, MAX_DATA_FIELD_POSTFIX, MIN_DATA_FIELD_POSTFIX, ExtTimeSeriesAggregation, getExtTimeSeriesAggregationTypeName,
    useConvertMultiSeriesToMultiMetricsDataPoints, useMergeMetricMultiSeriesDataPoints, getTimeSeriesResolutionName } from './tsUtils';
import { LazyComp } from '..';


// Maximum number of numeric/tag metrics we will show initially. This is to prevent the page from showing too many metrics on load when
// a collection has a lot of valueNames or tagNames. Once the page is loaded, the user can select more metrics if they want to
const MAX_INITIAL_NUMERIC_METRICS = 4;  // Numeric metrics is shown as line charts so it is not very useful to show too many lines
const MAX_INITIAL_TAG_METRICS = 6;      // Tag metrics is shown row by row so it is ok to show more tag metrics


type GridItemSizeValue = boolean | 12 | 6 | 4 | 3 | 'auto' | 2 | 1 | 10 | 5 | 7 | 8 | 9 | 11 | undefined;

const chartSizeToScreenSize: {[chartSize: string]: {[screenSize: string]: GridItemSizeValue}} = Object.freeze({
    [ChartSize.LARGE]: {
        xs: 12, sm: 12, md: 12, lg: 12, xl: 12,
    },
    [ChartSize.MEDIUM]: {
        xs: 12, sm: 12, md: 12, lg: 6, xl: 4,
    },
    [ChartSize.SMALL]: {
        xs: 12, sm: 12, md: 6, lg: 4, xl: 3,
    },
});



/**
 * Container style
 */
const useStyle = makeStyles((theme: Theme) => {

    return {
        root: {
            marginBottom: theme.spacing(12),
        },

        toolBar: {
            padding      : theme.spacing(2, 0),
            display      : 'flex',
            flexDirection: 'row',
            alignItems   : 'center',
            flexWrap     : 'wrap',
        },

        toolBarTitle: {
            fontSize  : 18,
            fontWeight: 500,
            whiteSpace: 'nowrap',
        },

        timeSeriesFilterMenu: {
            display   : 'flex',
            alignItems: 'center',
        },

        timeSeriesFilterTogglerButton: {
            whiteSpace: 'nowrap',
        },

        timeSeriesFilterMenuItemGroupTitle: {
            padding       : theme.spacing(1),
            display       : 'flex',
            alignItems    : 'center',
            justifyContent: 'center',
            color         : theme.palette.text.secondary,

            '& .icon': {
                marginRight: theme.spacing(1),
            },
        },

        timeSeriesFilterMenuItem: {
            minWidth: 200,
        },

        timeSeriesFilterMenuItemIcon: {
            minWidth: 'unset',
        },

        filterLabel: {
            margin    : theme.spacing(1),
            fontWeight: 'bold',
            whiteSpace: 'nowrap',
        },

        dateInputDialog: {
            width: 400,
        },

        dataCountInputDialog: {
            width: 400,
        },

        chartPanel: {
            height: 'auto',
        },

        chart: {
            width : '100%',
            height: '100%',
        },


        timeControlBarContainer: {
            position  : 'fixed',
            zIndex    : 1001,
            bottom    : 0,
            left      : 0,
            right     : 0,
            width     : '100%',
            background: `linear-gradient(to bottom, ${alpha(theme.palette.text.primary, 0)} 0%, 
                                ${alpha(theme.palette.background.paper, 1)} 100%)`,
            padding       : theme.spacing(3, 2),
            display       : 'flex',
            justifyContent: 'center',
            alignContent  : 'center',
            pointerEvents : 'none',

            [theme.breakpoints.up('md')]: {
                paddingLeft : 80,
                paddingRight: 20,
            },

            [theme.breakpoints.up('lg')]: {
                paddingLeft : 270,
                paddingRight: 20,
            },
        },

        timeControlBar: {
            display      : 'flex',
            padding      : theme.spacing(2),
            pointerEvents: 'auto',
            alignItems   : 'center',
            flexDirection: 'row',
            '& .input'   : {
                width                : 'fit-content',
                '&:not(:first-child)': {
                    marginLeft: theme.spacing(2),
                },
                '&.data-count': {
                    minWidth: 150,
                },
                '&.data-count-order': {
                    minWidth: 100,
                },
                '&.resolution': {
                    maxWidth: 100,
                },
                '&.zoom-buttons': {
                    display       : 'flex',
                    flexDirection : 'row',
                    alignItems    : 'center',
                    justifyContent: 'center',
                },
            },
        },

        noDataMessage: {
            display       : 'flex',
            flexDirection : 'column',
            alignItems    : 'center',
            justifyContent: 'center',
            flex          : 1,
        },
    };
}, { name: 'MetricsDataViewContainer', index: 2 });



/**
 * Search params used for this TimeSeriesDataDisplayContainer
 */
interface DataSeriesSearchParams {
    readonly selectedNumericMetrics: readonly NumericMetricInfo[];
    readonly selectedTagMetrics: readonly TagMetricInfo[];

    // Use for fetching aggregated data
    readonly start: number;
    readonly end: number;

    // Use for fetching raw data
    readonly timestamp: number;
    readonly limit: number;
    readonly beforeTimestamp: boolean;      // Used for fetching RAW data. True means to fetch -$limit data points and false mean to fetch $limit
}



/**
 * This is the set of options the user has for viewing time series data.
 * - The user can turn on/off Chart or tabular views
 * - The user can combine/uncombined multiple series into the same chart
 * - The user can turn on/off bullets for charts
 */
interface TimeSeriesViewOptions {
    readonly showChartView: boolean;
    readonly showTabularView: boolean;
    readonly showChartBullets: boolean;
    readonly combineSeries: boolean;                    // whether or not to combine multiple series into 1 chart
    readonly chartSize: ChartSize;
    readonly aggregation: ExtTimeSeriesAggregation | undefined;
}


/**
 * The default values for TimeSeriesViewOptions
 */
const DEFAULT_TIME_SERIES_VIEW_OPTIONS: TimeSeriesViewOptions = Object.freeze({
    showChartView   : true,
    showTabularView : false,
    showChartBullets: false,
    combineSeries   : true,
    chartSize       : ChartSize.LARGE,
    aggregation     : undefined,
});


const MIN_DATA_SIZE: number = 50;
const MAX_DATA_SIZE: number = 500;


/**
 * Custom hook to create a table columns for each selected metrics. These columns will be used for the time series tabular display
 */
const useCreateTableColumnsForSelectedMetrics = (
    trans: TFunction, selectedNumericMetrics: readonly NumericMetricInfo[], selectedTagMetrics: readonly TagMetricInfo[],
): TimeSeriesTableCompField[] => {
    const [tableColumns, setTableColumn] = useState<TimeSeriesTableCompField[]>([]);

    useEffect(() => {

        if (selectedNumericMetrics.length > 0 || selectedTagMetrics.length > 0) {
            // Create an array of columns for each selected metric
            const columns: TimeSeriesTableCompField[] = [
                // for each selected numeric metric, add another column
                ...selectedNumericMetrics.map((numericMetric: NumericMetricInfo) => {
                    return {
                        dataItemProp: numericMetric.name,
                        label       : numericMetric.displayName || numericMetric.name,
                        unit        : numericMetric.unit,
                    } as TimeSeriesTableCompField;
                }),
                // for each selected tag metric, add another column
                ...selectedTagMetrics.map((tagMetric: TagMetricInfo) => {
                    return {
                        dataItemProp: tagMetric.name,
                        label       : tagMetric.displayName || tagMetric.name,
                    } as TimeSeriesTableCompField;
                }),
            ];
            setTableColumn(columns);
        } else {
            setTableColumn([]);
        }

    }, [trans, selectedTagMetrics, selectedNumericMetrics]);

    return tableColumns;
};



/**
 * Props for TimeSeriesDataDisplayContainer. To use TimeSeriesDataDisplayContainer, the container of this container needs to provide these data
 *
 * absoluteStartDate - The timestamp of the VERY FIRST data point of all the time series, not just 1 specific series.
 * dataLimit - The maximum number of data point to fetch
 * allMetrics - The complete list of all metrics the device/module has, this includes metrics that are selected and not selected.
 * multiSeriesDataPoints - The Array of data points of merged time series data.
 * dataResolution - The resolution (in milliseconds) of the data points
 * fetchTimeSeriesDataHandler - The callback to handle fetching data. This container does not know how to fetch data. It is the container of this
 *                              container's responsibility to fetch data and update multiSeriesDataPoints.
 */
export interface MetricsDataDisplayContainerProps {
    readonly className?: string;
    readonly projectId: number;
    readonly absoluteStartDate: number;
    readonly absoluteEndDate: number;
    readonly dataLimit?: number;
    readonly numericMetrics?: readonly NumericMetricInfo[] | undefined;
    readonly multiSeriesNumericDataPoints: readonly MultiSeriesDataPoint<number>[];
    readonly tagMetrics?: readonly TagMetricInfo[] | undefined;
    readonly multiSeriesTagDataPoints: readonly MultiSeriesDataPoint<string>[];
    readonly dataResolution?: TimeSeriesResolution | undefined;
    readonly fetchTimeSeriesDataHandler: (
        selectedNumericMetrics: readonly NumericMetricInfo[], selectedTagMetrics: readonly TagMetricInfo[],
        aggregation: ExtTimeSeriesAggregation | undefined,
        start: number,
        end: number | undefined,        // undefined if aggregation type IS undefined
        limit: number | undefined,      // undefined if aggregation type IS NOT undefined
    )=> void;
    readonly viewTypeSettings?: {
        chart?: {
            readonly excludedMetricsNames?: readonly string[];
        },
        tabular?: {
            readonly excludedMetricsNames?: readonly string[];
        }
    };
    readonly viewOptions?: {
        readonly showChartView?: boolean | undefined;
        readonly showTabularView?: boolean | undefined;
        readonly showChartBullets?: boolean | undefined;
        readonly combineSeries?: boolean | undefined;                    // whether or not to combine multiple series into 1 chart
        readonly chartSize?: ChartSize | undefined;
        readonly aggregation?: ExtTimeSeriesAggregation | undefined;
    };
}


/**
 * Container for displaying raw time series data in chart and tabular form
 */
export const MetricsDataDisplayContainer: React.FC<MetricsDataDisplayContainerProps> = (props: MetricsDataDisplayContainerProps) => {

    const { state:uiState, dispatch:uiDispatch } = useContext(modeUIContext);
    const { state:dataState } = useContext(ritzDataContext);
    const loggedInAgent = selectLoggedInAgent(dataState) as Agent;
    const project = selectProjectById(dataState, props.projectId);
    const selectedLanguage = getLanguageByCode(
        Constants.SUPPORTED_LANGUAGES, selectLanguage(uiState),
    ) || Constants.DEFAULT_LANGUAGE;
    const currentLanguage = loggedInAgent
        ? Utils.getPreferredLanguage(project, loggedInAgent, Constants.DEFAULT_LANGUAGE)
        : selectedLanguage;
    const { trans } = useRitzTranslation(project);
    const { fetchTimeSeriesDataHandler } = props;



    // Set state of the view options for the time series views. Initialize it with the default option values
    const [timeSeriesViewOptions, setTimeSeriesViewOptions] = useState<TimeSeriesViewOptions>(
        { ...DEFAULT_TIME_SERIES_VIEW_OPTIONS, ...props.viewOptions },
    );

    // Set up the searchParams use for filtering time series display
    const {
        searchParams, onOtherFieldsChange, onItemsPerPageChange, onPrevPage, onNextPage, onPage,
    } = useSearchParams<DataSeriesSearchParams>({
        searchText : '',
        otherFields: {
            start                 : Math.max(props.absoluteStartDate, props.absoluteEndDate - Time.HOUR_IN_MS),
            end                   : props.absoluteEndDate,
            timestamp             : props.absoluteEndDate,
            limit                 : props.dataLimit || 120,
            beforeTimestamp       : true,
            selectedNumericMetrics: [...(props.numericMetrics || []).slice(0, MAX_INITIAL_NUMERIC_METRICS)],
            selectedTagMetrics    : [...(props.tagMetrics || []).slice(0, MAX_INITIAL_TAG_METRICS)],
        },
        itemsPerPage: ITEMS_PER_PAGE_20,
        currentPage : 0,
    });

    // Get the list of numeric metrics to be displayed per chart base on the current selected numeric metrics and whether or not the user want to
    // combine charts
    const numericMetricsPerChart = useRebuildMetricsPerChartArray(
        searchParams.otherFields.selectedNumericMetrics, timeSeriesViewOptions.combineSeries, props.viewTypeSettings?.chart?.excludedMetricsNames,
    );

    // Get the list of tag metrics to be displayed per chart base on the current selected tag metrics and whether or not the user want to
    // combine charts
    const tagMetricsPerChart = useRebuildMetricsPerChartArray(
        searchParams.otherFields.selectedTagMetrics, timeSeriesViewOptions.combineSeries, props.viewTypeSettings?.chart?.excludedMetricsNames,
    );

    // The states of the option dropdown menu. There are many options therefore we will group them into group. The first level of this menu
    // will show the list of option group e.g. "View Type", "Aggregation Type", "Chart Options". Clicking on one of these item will expand
    // the menu and show the sub menu item for each group
    const [viewOptionsMenuAnchorEl, isViewOptionsMenuOpened, onViewOptionsMenuAnchorClicked, onViewOptionsMenuClosed] = useDropdown();
    const [viewTypesMenuAnchorEl, isViewTypesMenuOpened, onViewTypesMenuAnchorClicked, onViewTypesMenuClosed] = useDropdown();
    const [aggrTypesMenuAnchorEl, isAggrTypesMenuOpened, onAggrTypesMenuAnchorClicked, onAggrTypesMenuClosed] = useDropdown();
    const [chartOptionsMenuAnchorEl, isChartOptionsMenuOpened, onChartOptionsMenuAnchorClicked, onChartOptionsMenuClosed] = useDropdown();

    // The state of the select metrics dropdown menu. There will be 2 levels for this menu, the first level shows 2 options, Numeric Metrics
    // and Tag Metrics. Clicking on one of these will show the second level menu which show the list of specific Numeric/Tag Metrics.
    const [metricsMenuAnchorEl, isMetricsMenuOpened, onMetricsMenuAnchorClicked, onMetricsMenuClosed] = useDropdown();
    const [numericMetricsMenuAnchorEl, isNumericMetricsMenuOpened, onNumericMetricsMenuAnchorClicked, onNumericMetricsMenuClosed] = useDropdown();
    const [tagMetricsMenuAnchorEl, isTagMetricsMenuOpened, onTagMetricsMenuAnchorClicked, onTagMetricsMenuClosed] = useDropdown();

    // The start date the user selected from the chart
    const [chartViewStartDate, setChartViewStartDate] = useState<number>();

    // To prevent from fetching time series data was soon as the chart start date changed, we will debounce the chartViewStartDate change for
    // about N seconds. After N seconds, the debouncedChartViewStartDate will be changed by the useDebounce and that will trigger the use effect
    // to request for new data.
    const [debouncedChartViewStartDate] = useDebounce(chartViewStartDate, 5000);

    // Because of useEffect dependencies, sometime the chart will be re-rendered when one of the dependencies changed that don't actually
    // change the chart data. We need this state to make sure that when the useEffect is called, it is because debouncedChartDateRange
    // changed by comparing debouncedChartDateRange with previousDebouncedChartDateRange.
    const [previousDebouncedChartViewStartDate, setPreviousDebouncedChartViewStartDate] = useState<number>();


    // Merge both filtered data points into 1 array so we can show them in 1 table
    const mergedMultiSeriesDataPoints = useMergeMetricMultiSeriesDataPoints<number | string>(
        props.multiSeriesNumericDataPoints, props.multiSeriesTagDataPoints,
    );


    // The TimeSeriesDataTable only work with MultiMetricsDataPoint data array so we need to convert the "mergedMultiSeriesDataPoints" array
    // to "MultiMetricsDataPoint" array
    const multiMetricsDataPoints = useConvertMultiSeriesToMultiMetricsDataPoints(
        mergedMultiSeriesDataPoints,
    );


    // We don't display all data but only part of the data based on the current page so need to filter out the data points we don't need to show
    // This is used by the Table because it has Pagination.
    const filteredMetricsDataPoints = useMemo(() => {
        if (multiMetricsDataPoints) {
            const startIndex = searchParams.currentPage * searchParams.itemsPerPage;
            return multiMetricsDataPoints.slice(
                startIndex, startIndex + searchParams.itemsPerPage,
            );
        }
        return undefined;
    }, [multiMetricsDataPoints, searchParams.currentPage, searchParams.itemsPerPage]);



    // The BaseListCompField for the selected metrics. This is used for tabular display. When we create the TimeSeriesDataTableComp, we don't
    // know what columns we need to setup because this is dynamic base on the metrics the user selected. So this is why we need this state.
    // This state will monitor the "searchParams.otherFields.selectedMetrics" state and will automatically create an Array of table columns
    // for the selected metrics.
    const tableColumnsForSelectedMetrics = useCreateTableColumnsForSelectedMetrics(
        trans, searchParams.otherFields.selectedNumericMetrics, searchParams.otherFields.selectedTagMetrics,
    );


    /**
     * amChart doesn't display Error chart correctly. It draws the error bar outside of the chart area sometime. So the work around is to
     * calculate the metrics' data range, the lowest and highest value for each metric, and force amChart to use those as Y axis bounds.
     * But we only need this for AVG_MIN_MAX aggregation type. For other aggregation type, we let the amChart decide the range base on the values
     * multiSeriesNumericDataPoints is in this format
     *      [{
     *          date: "...",
     *          values: {
     *              temperature: 1, temperature_min: 0, temperature_max:2,
     *              pressure:10, pressure_min:5, pressure_max:15
     *          }
     *      }, {
     *          date: "...",
     *          values: {
     *              temperature: 20, temperature_min: 3, temperature_max:30,
     *              pressure:34, pressure_min:30, pressure_max:40
     *          }
     *      }]
     *
     * To calculate the data range, we will loop through the array of multiSeriesNumericDataPoints and find the min and max value for each
     * metric. Note that because AVG_MIN_MAX aggregation actually has 3 set of data so when we calculate the data range, we need to combine
     * avg, max, and max. The result will be something like this
     *
     *      {
     *          temperature: {min:0, max: 30},
     *          pressure: {5, max: 40}
     *      }
     */
    const metricsValueRange = useMemo((): XYChartProps['yAxesRange'] => {
        if (timeSeriesViewOptions.aggregation === ExtTimeSeriesAggregation.AVG_MIN_MAX) {

            return props.multiSeriesNumericDataPoints.reduce((
                result, multiMetricDataPoint,
            ): { readonly [metricName: string]: { readonly min?: number | undefined; readonly max?: number | undefined; } } => {
                Object.keys(multiMetricDataPoint.values).forEach((metricName) => {
                    const value = multiMetricDataPoint.values[metricName];
                    if (typeof value === 'number') {
                        // Each metric has 3 set of data, avg, min, and max. For AVG data, we use the metric's name as is, we didn't add
                        // anything to the end of the metric's name. For min and max, we added a string to the end of the metric's name e.g.
                        // _min, _max therefore to get the actual metric's name, we need to remove the postfix we added.
                        const actualMetricName = (() => {
                            if (metricName.endsWith(MIN_DATA_FIELD_POSTFIX)) {
                                return metricName.substr(0, metricName.length - MIN_DATA_FIELD_POSTFIX.length);
                            }
                            if (metricName.endsWith(MAX_DATA_FIELD_POSTFIX)) {
                                return metricName.substr(0, metricName.length - MAX_DATA_FIELD_POSTFIX.length);
                            }
                            return metricName;
                        })();

                        if (!result[actualMetricName]) {
                            // If this is the first time we see data for the metric then the current value will be the min/max
                            // eslint-disable-next-line no-param-reassign
                            result[actualMetricName] = {
                                min: value, max: value,
                            };
                        } else {
                            // If we have already min/max value for the metric then compare the current value with the min/max value we already have
                            // and use the lowest value for min and highest value for max
                            // eslint-disable-next-line no-param-reassign
                            result[actualMetricName] = {
                                min: Math.min(value, result[actualMetricName].min),
                                max: Math.max(value, result[actualMetricName].max),
                            };
                        }
                    }
                });
                return result;
            }, {});
        }
        return undefined;
    }, [props.multiSeriesNumericDataPoints, timeSeriesViewOptions.aggregation]);


    // Temporary states for the time control. When the user make changes to the start/end time, dataCount, etc... we will
    // not apply these changes right away. We will store these changes in these states. Once the user is done and click 'Apply',
    // we will then apply these changes.
    const [startTimeInput, setStartTimeInput] = useState<MaterialUiPickersDate>(moment(searchParams.otherFields.start));
    const [endTimeInput, setEndTimeInput] = useState<MaterialUiPickersDate>(moment(searchParams.otherFields.end));
    const [timestampInput, setTimestampInput] = useState<MaterialUiPickersDate>(moment(searchParams.otherFields.timestamp));
    const [dataCountInput, setDataCountInput] = useState<number>(searchParams.otherFields.limit);
    const [beforeTimestamp, setBeforeTimestamp] = useState<boolean>(searchParams.otherFields.beforeTimestamp);



    const panelClasses = useModePanelStyle();
    const classes = useStyle();



    /**
     * This use effect is used for monitoring search params' selectedMetrics, start date and limit changes and notify the container to fetch data
     */
    useEffect(() => {
        const { start, end, timestamp, beforeTimestamp: before, limit, selectedNumericMetrics, selectedTagMetrics } = searchParams.otherFields;
        if (selectedNumericMetrics.length > 0 || selectedTagMetrics.length > 0) {
            // We need to use different attribute depending on the current settings. If we need to fetch RAW data then we
            // will use the 'timestamp' attribute. Else we use the 'start' attribute.
            // As for limit, if 'beforeTimestamp' is true then we need to fetch NEGATIVE limit value. Else we fetch POSITIVE limit value
            const useStartTime = timeSeriesViewOptions.aggregation === undefined ? timestamp : start;
            const useLimit = before ? limit * -1 : limit;

            // call the container to fetch time series data
            fetchTimeSeriesDataHandler(
                selectedNumericMetrics,
                selectedTagMetrics,
                timeSeriesViewOptions.aggregation,
                useStartTime,
                end,
                useLimit,
            );
        }
    }, [searchParams.otherFields, fetchTimeSeriesDataHandler, timeSeriesViewOptions.aggregation]);



    /**
     * On show charts view toggler clicked
     */
    const onToggleShowChartView = useCallback(() => {
        setTimeSeriesViewOptions((currentViewOptions) => {
            return {
                ...currentViewOptions,
                showChartView: !currentViewOptions.showChartView,
            };
        });
    }, [setTimeSeriesViewOptions]);


    /**
     * On show tabular view toggler clicked
     */
    const onToggleShowTabularView = useCallback(() => {
        setTimeSeriesViewOptions((currentViewOptions) => {
            return {
                ...currentViewOptions,
                showTabularView: !currentViewOptions.showTabularView,
            };
        });
    }, [setTimeSeriesViewOptions]);


    /**
     * On Chart aggregation type changed
     */
    const onSetAggregationType = useCallback((aggregation: ExtTimeSeriesAggregation | undefined) => {
        setTimeSeriesViewOptions((currentViewOptions) => {
            return {
                ...currentViewOptions,
                aggregation,
            };
        });

        if (props.multiSeriesNumericDataPoints.length > 0) {
            if (timeSeriesViewOptions.aggregation === undefined && aggregation !== undefined) {
                // If the user switch from RAW to Aggregated view which mean now we need to fetch data with start/end time. To make it more intuitive,
                // we will automatically use the current RAW data's time interval as the start/end time to fetch aggregated data instead of
                // using the last start/end time that the user selected. For example, the user is looking at RAW data from Oct 1 to Oct 2 and then
                // switch to AVG aggregated data view, we will automatically fetch aggregated data from Oct 1 to Oct 2.
                const startTime = props.multiSeriesNumericDataPoints[0].dateValue;

                // If there are more than 1 data points than use the last data point's date as endTime. If there is only 1 data point then
                // use the startTime + 1 min as endTime. We can't use the last data point's time because it will be the same as startTime.
                const endTime = props.multiSeriesNumericDataPoints.length > 1
                    ? props.multiSeriesNumericDataPoints[props.multiSeriesNumericDataPoints.length - 1].dateValue
                    : startTime + Time.MINUTE_IN_MS;

                // Update the state/end time to trigger the data to be reloaded
                onOtherFieldsChange(produce(searchParams.otherFields, (draft: Draft<DataSeriesSearchParams>) => {
                    draft.start = startTime;
                    draft.end = endTime;
                }));

                // We need to update the start/end time controller to show the new start/end time
                setStartTimeInput(moment(startTime));
                setEndTimeInput(moment(endTime));
            } else {
                // If the user switch from Aggregated data view to RAW data view which mean we need to fetch data with a timestamp and count.
                // To make it more intuitive, we will automatically use the aggregated data's start/end time as the start time to fetch raw data.
                // However, aggregated data has 2 timestamps, start/end, while raw data only have 1 so we need to pick 1 of the timestamps.
                // Naturally, the user looks at data from left to right so we will use the aggregated data's startTime and the raw data's timestamp
                // AND we will use positive data count (AFTER timestamp).
                const startTime = props.multiSeriesNumericDataPoints[0].dateValue;

                onOtherFieldsChange(produce(searchParams.otherFields, (draft: Draft<DataSeriesSearchParams>) => {
                    draft.timestamp = startTime;
                    draft.beforeTimestamp = false;
                }));

                // We need to update the timestamp and beforeTimestamp so that the controller will show the new values
                setTimestampInput(moment(startTime));
                setBeforeTimestamp(false);
            }
        }
    }, [onOtherFieldsChange, props.multiSeriesNumericDataPoints, searchParams.otherFields, timeSeriesViewOptions.aggregation]);


    /**
     * On combine series toggler clicked
     */
    const onToggleCombineSeries = useCallback(() => {
        setTimeSeriesViewOptions((currentViewOptions) => {
            return {
                ...currentViewOptions,
                combineSeries: !currentViewOptions.combineSeries,
            };
        });
    }, [setTimeSeriesViewOptions]);


    /**
     * On show chart bullet toggler clicked
     */
    const onToggleShowChartBullets = useCallback(() => {
        setTimeSeriesViewOptions((currentViewOptions) => {
            return {
                ...currentViewOptions,
                showChartBullets: !currentViewOptions.showChartBullets,
            };
        });
    }, [setTimeSeriesViewOptions]);



    /**
     * On Chart Size clicked
     */
    const onSetChartSize = useCallback((size: ChartSize) => {
        setTimeSeriesViewOptions((currentViewOptions) => {
            return {
                ...currentViewOptions,
                chartSize: size,
            };
        });
    }, [setTimeSeriesViewOptions]);


    /**
     * Handler for the select/unselect all NUMERIC metrics checkbox
     */
    const onToggleSelectAllNumericMetrics = useCallback(() => {
        if ((!props.numericMetrics || searchParams.otherFields.selectedNumericMetrics.length >= props.numericMetrics.length)) {
            // all metrics are selected so we need to unselect all
            onOtherFieldsChange(produce(searchParams.otherFields, (draft: Draft<DataSeriesSearchParams>) => {
                draft.selectedNumericMetrics = [];
            }));
        } else {
            // one or more metrics are not selected, select all
            onOtherFieldsChange(produce(searchParams.otherFields, (draft: Draft<DataSeriesSearchParams>) => {
                draft.selectedNumericMetrics = castDraft([...(props.numericMetrics || [])]);
            }));
        }
    }, [props.numericMetrics, searchParams.otherFields, onOtherFieldsChange]);


    /**
     * Handler for the select/unselect all TAG metrics checkbox
     */
    const onToggleSelectAllTagMetrics = useCallback(() => {
        if ((!props.tagMetrics || searchParams.otherFields.selectedTagMetrics.length >= props.tagMetrics.length)) {
            // all metrics are selected so we need to unselect all
            onOtherFieldsChange(produce(searchParams.otherFields, (draft: Draft<DataSeriesSearchParams>) => {
                draft.selectedTagMetrics = [];
            }));
        } else {
            // one or more metrics are not selected, select all
            onOtherFieldsChange(produce(searchParams.otherFields, (draft: Draft<DataSeriesSearchParams>) => {
                draft.selectedTagMetrics = castDraft([...(props.tagMetrics || [])]);
            }));
        }
    }, [props.tagMetrics, searchParams.otherFields, onOtherFieldsChange]);


    /**
     * select/unselect a numeric metric.
     */
    const onToggleSelectedNumericMetric = useCallback((numericMetric: NumericMetricInfo) => {
        if (searchParams.otherFields.selectedNumericMetrics.find((metric: NumericMetricInfo) => {
            return metric.name === numericMetric.name;
        })) {
            onOtherFieldsChange(produce(searchParams.otherFields, (draft: Draft<DataSeriesSearchParams>) => {
                draft.selectedNumericMetrics = castDraft([
                    ...searchParams.otherFields.selectedNumericMetrics.filter((nm: NumericMetricInfo): boolean => {
                        return nm !== numericMetric;
                    }),
                ]);
            }));
        } else {
            onOtherFieldsChange(produce(searchParams.otherFields, (draft: Draft<DataSeriesSearchParams>) => {
                draft.selectedNumericMetrics = castDraft([
                    ...searchParams.otherFields.selectedNumericMetrics,
                    numericMetric,
                ]);
            }));
        }
    }, [searchParams.otherFields, onOtherFieldsChange]);


    /**
     * select/unselect a tag metric.
     */
    const onToggleSelectedTagMetric = useCallback((tagMetric: TagMetricInfo) => {
        if (searchParams.otherFields.selectedTagMetrics.find((metric: TagMetricInfo) => {
            return metric.name === tagMetric.name;
        })) {
            onOtherFieldsChange(produce(searchParams.otherFields, (draft: Draft<DataSeriesSearchParams>) => {
                draft.selectedTagMetrics = castDraft([
                    ...searchParams.otherFields.selectedTagMetrics.filter((tm: TagMetricInfo): boolean => {
                        return tm !== tagMetric;
                    }),
                ]);
            }));
        } else {
            onOtherFieldsChange(produce(searchParams.otherFields, (draft: Draft<DataSeriesSearchParams>) => {
                draft.selectedTagMetrics = castDraft([
                    ...searchParams.otherFields.selectedTagMetrics,
                    tagMetric,
                ]);
            }));
        }
    }, [searchParams.otherFields, onOtherFieldsChange]);



    /**
     * Update the time series view date range. This will trigger fetch data.
     */
    const updateTimeSeriesViewStartDate = useCallback((start: number) => {
        // make sure the new value is different because updating the state or else the chart will re-render for no reason
        if (start !== searchParams.otherFields.start) {
            onOtherFieldsChange(produce(searchParams.otherFields, (draft: Draft<DataSeriesSearchParams>) => {
                draft.start = start;
            }));
        }
    }, [onOtherFieldsChange, searchParams.otherFields]);



    /**
     * On chart zoom/pan event
     */
    const onChartZoomAndPan = useCallback((startTime: number, endTime: number) => {
        if (timeSeriesViewOptions.aggregation !== undefined && props.dataResolution !== undefined) {
            // We are in Aggregated data view mode so we only need to update these attributes
            onOtherFieldsChange(produce(searchParams.otherFields, (draft: Draft<DataSeriesSearchParams>) => {
                draft.start = startTime;
                draft.end = endTime;
            }));

            // We need to update the start/end time controller to show the new start/end time
            setStartTimeInput(moment(startTime));
            setEndTimeInput(moment(endTime));
        }
    }, [onOtherFieldsChange, props.dataResolution, searchParams.otherFields, timeSeriesViewOptions.aggregation]);



    const onChartClick = useCallback((timestamp: number) => {
        onOtherFieldsChange(produce(searchParams.otherFields, (draft: Draft<DataSeriesSearchParams>) => {
            draft.start = timestamp;
        }));
    }, [onOtherFieldsChange, searchParams.otherFields]);


    /**
     * Chart setting to be passed to the TimeSeriesChartComponent
     */
    const [chartSettings, setChartSettings] = useState<ChartSettings>({
        className: clsx(classes.chart),
        locale   : currentLanguage.locale2,
    });



    useEffect(() => {
        if (debouncedChartViewStartDate
            && (!previousDebouncedChartViewStartDate || previousDebouncedChartViewStartDate !== debouncedChartViewStartDate)) {

            setPreviousDebouncedChartViewStartDate(debouncedChartViewStartDate);
            updateTimeSeriesViewStartDate(debouncedChartViewStartDate);
        }
    }, [debouncedChartViewStartDate, previousDebouncedChartViewStartDate, updateTimeSeriesViewStartDate]);



    /**
     * This use effect is used for updating the chartSettings when one of the dependencies changed
     */
    useEffect(() => {
        setChartSettings(produce(chartSettings, (draft: Draft<XYChartProps>) => {
            draft.showLegends = timeSeriesViewOptions.combineSeries;
            draft.locale = currentLanguage.locale2;
            draft.showBullets = timeSeriesViewOptions.showChartBullets;
        }));
    }, [chartSettings, timeSeriesViewOptions.combineSeries, currentLanguage.locale2, timeSeriesViewOptions.showChartBullets]);



    /**
     * Create chart views numeric metrics
     */
    const createNumericMetricChartViews = useCallback(() => {

        return (
            <>
                {numericMetricsPerChart.map((metrics: NumericMetricInfo[], index: number) => {
                    return (
                        <Grid
                            item
                            xs={!timeSeriesViewOptions.combineSeries
                                ? chartSizeToScreenSize[timeSeriesViewOptions.chartSize].xs : 12}
                            sm={!timeSeriesViewOptions.combineSeries
                                ? chartSizeToScreenSize[timeSeriesViewOptions.chartSize].sm : 12}
                            md={!timeSeriesViewOptions.combineSeries
                                ? chartSizeToScreenSize[timeSeriesViewOptions.chartSize].md : 12}
                            lg={!timeSeriesViewOptions.combineSeries
                                ? chartSizeToScreenSize[timeSeriesViewOptions.chartSize].lg : 12}
                            xl={!timeSeriesViewOptions.combineSeries
                                ? chartSizeToScreenSize[timeSeriesViewOptions.chartSize].xl : 12}

                            key={metrics.map((s) => { return (s.name); }).join('-')}
                        >
                            <TimeSeriesChartPanel
                                className={clsx(!timeSeriesViewOptions.combineSeries && classes.chartPanel,
                                    !timeSeriesViewOptions.combineSeries && timeSeriesViewOptions.chartSize)}
                                title={timeSeriesViewOptions.combineSeries
                                    ? capitalizeAll(trans('label.time_series_view.numeric.charts'))
                                    : metrics[0].displayName || metrics[0].name}
                                icon="show_chart"
                                dataCount={props.multiSeriesNumericDataPoints.length}
                                seriesCount={metrics.length}
                                noDataMessageComp={(
                                    <GenericMessageBox
                                        className={classes.noDataMessage}
                                        title={trans('generic.no_data.title', {
                                            term: capitalizeAll(trans('label.time_series_data', { count: 2 })),
                                        })}
                                    />
                                )}
                            >
                                <LazyComp>
                                    <XYChart
                                        {...chartSettings}
                                        chartType={timeSeriesViewOptions.aggregation === ExtTimeSeriesAggregation.AVG_MIN_MAX
                                            ? XYChartType.AVG_MIN_MAX : undefined}
                                        showYAxes={metrics.length <= 4}
                                        avgMinMaxChartSettings={{
                                            minDataFieldPostfix: MIN_DATA_FIELD_POSTFIX,
                                            maxDataFieldPostfix: MAX_DATA_FIELD_POSTFIX,
                                            avgLabel           : capitalizeAll(trans('label.average_abbrev')),
                                            minLabel           : capitalizeAll(trans('label.minimum_abbrev')),
                                            maxLabel           : capitalizeAll(trans('label.maximum_abbrev')),
                                        }}
                                        dateRange={{
                                            resolution: props.dataResolution ? resolutionToMilliseconds(props.dataResolution as string) : undefined,
                                        }}
                                        chartId={`TimeSeriesDataDisplayContainer:chart-id-${index}`}
                                        dataPoints={props.multiSeriesNumericDataPoints}
                                        yAxesRange={metricsValueRange}
                                        displayMetrics={metrics}
                                        // If viewing aggregated data, we will let the user zoom in to the data using mouse and then automatically
                                        // fetch new data as the user zoom in.
                                        onChartZoomAndPan={timeSeriesViewOptions.aggregation !== undefined ? onChartZoomAndPan : undefined}
                                        // If viewing aggregated data but backend doesn't return resolution, that mean there is no more detailed
                                        // aggregated data so we don't need to allow zoom in anymore
                                        disableZoom={timeSeriesViewOptions.aggregation !== undefined && props.dataResolution === undefined}
                                    />
                                </LazyComp>
                            </TimeSeriesChartPanel>
                        </Grid>
                    );
                })}
            </>
        );
    }, [numericMetricsPerChart, timeSeriesViewOptions.combineSeries, timeSeriesViewOptions.chartSize, timeSeriesViewOptions.aggregation,
        classes.chartPanel, classes.noDataMessage, trans, props.multiSeriesNumericDataPoints, props.dataResolution, chartSettings, metricsValueRange,
        onChartZoomAndPan]);



    /**
     * Create chart views for the Tag metrics
     */
    const createTagMetricChartViews = useCallback(() => {

        return (
            <>
                {tagMetricsPerChart.map((metrics: TagMetricInfo[], index: number) => {
                    const data = props.multiSeriesTagDataPoints;

                    return (
                        <Grid
                            item
                            xs={!timeSeriesViewOptions.combineSeries
                                ? chartSizeToScreenSize[timeSeriesViewOptions.chartSize].xs : 12}
                            sm={!timeSeriesViewOptions.combineSeries
                                ? chartSizeToScreenSize[timeSeriesViewOptions.chartSize].sm : 12}
                            md={!timeSeriesViewOptions.combineSeries
                                ? chartSizeToScreenSize[timeSeriesViewOptions.chartSize].md : 12}
                            lg={!timeSeriesViewOptions.combineSeries
                                ? chartSizeToScreenSize[timeSeriesViewOptions.chartSize].lg : 12}
                            xl={!timeSeriesViewOptions.combineSeries
                                ? chartSizeToScreenSize[timeSeriesViewOptions.chartSize].xl : 12}

                            key={metrics.map((s) => { return (s.name); }).join('-')}
                        >
                            <TimeSeriesChartPanel
                                className={clsx(!timeSeriesViewOptions.combineSeries && classes.chartPanel,
                                    !timeSeriesViewOptions.combineSeries && timeSeriesViewOptions.chartSize)}
                                title={timeSeriesViewOptions.combineSeries
                                    ? capitalizeAll(trans('label.time_series_view.tag.charts'))
                                    : metrics[0].displayName || metrics[0].name}
                                icon="show_chart"
                                dataCount={data.length}
                                seriesCount={metrics.length}
                                noDataMessageComp={(
                                    <GenericMessageBox
                                        className={classes.noDataMessage}
                                        title={timeSeriesViewOptions.aggregation === undefined
                                            ? trans('generic.no_data.title', {
                                                term: capitalizeAll(trans('label.time_series_data', { count: 2 })),
                                            })
                                            : trans('error.data_not_supported.message', {
                                                aggregation: capitalizeAll(
                                                    trans(`time_series.aggregation_type.${timeSeriesViewOptions.aggregation}`),
                                                ),
                                            })}
                                    />
                                )}
                            >
                                <LazyComp>
                                    <GanttChart
                                        {...chartSettings}
                                        disableZoom
                                        chartId={`TSCollectionTagsDisplayContainer:chart-id-${index}`}
                                        dateRange={{
                                            start     : searchParams.otherFields.start,
                                            end       : searchParams.otherFields.limit,
                                            resolution: props.dataResolution ? resolutionToMilliseconds(props.dataResolution as string) : undefined,
                                        }}
                                        dataPoints={data}
                                        displayMetrics={metrics}
                                    />
                                </LazyComp>
                            </TimeSeriesChartPanel>
                        </Grid>
                    );
                })}
            </>
        );
    }, [tagMetricsPerChart, props.multiSeriesTagDataPoints, props.dataResolution, timeSeriesViewOptions.combineSeries,
        timeSeriesViewOptions.chartSize, timeSeriesViewOptions.aggregation, classes.chartPanel, classes.noDataMessage, trans, chartSettings,
        searchParams.otherFields.start, searchParams.otherFields.limit]);



    const createChartViews = useCallback(() => {
        return (
            <>
                {createNumericMetricChartViews()}
                {createTagMetricChartViews()}
            </>
        );
    }, [createNumericMetricChartViews, createTagMetricChartViews]);



    const tableViewSettings = useCallback((): TimeSeriesDataTableProps => {
        const totalDataPoints = mergedMultiSeriesDataPoints?.length || 0;
        return {
            title   : capitalizeAll(trans('label.time_series_view.numeric.tabular')),
            icon    : 'view_column',
            listData: filteredMetricsDataPoints,

            // Dynamically add the columns to the table based on the list of selected metrics.
            fieldsSettings: {
                fields: {
                    date: {
                        dataItemProp: 'date',
                        label       : capitalizeAll(trans('label.date')),
                    },
                    ...tableColumnsForSelectedMetrics.reduce((
                        result: {[key: string]: TimeSeriesTableCompField},
                        columnSetting: TimeSeriesTableCompField,
                    ): {[key: string]: TimeSeriesTableCompField} => {
                        if (columnSetting.dataItemProp) {
                            return {
                                ...result,
                                [columnSetting.dataItemProp]: columnSetting,
                            };
                        }
                        return result;
                    }, {}),
                },
            },
            decimalPlaces : Constants.DEFAULT_DECIMAL_PLACES,
            locale        : currentLanguage.locale2,
            dateTimeFormat: {
                year                  : 'numeric',
                month                 : 'short',
                day                   : '2-digit',
                hour                  : '2-digit',
                minute                : '2-digit',
                second                : '2-digit',
                fractionalSecondDigits: 3,
                hour12                : false,
            },
            labels: {
                emptyValues: '-',
            },
            paginationComp: (
                <PaginationBar {...{
                    itemsPerPageList: ITEMS_PER_PAGE_LIST,
                    itemsPerPage    : searchParams.itemsPerPage,
                    pagingMinPages  : PAGING_MIN_PAGES,
                    currentPage     : searchParams.currentPage,
                    totalItems      : totalDataPoints,
                    hasMoreItems    : totalDataPoints > ((searchParams.currentPage + 1) * searchParams.itemsPerPage),
                    texts           : {
                        itemsPerPage: capitalizeAll(trans('label.paging.items_per_page', {
                            item_name: trans('label.item', { count: 2 }),
                        })),
                        pageNTemplate   : capitalizeAll(trans('label.page_n')),
                        pageNofMTemplate: capitalizeAll(trans('label.page_n_of_m')),
                    },
                    onItemsPerPageChange,
                    onNextPage,
                    onPrevPage,
                    onPage,
                }}
                />
            ),
            noDataMessageComp: (
                <GenericMessageBox
                    title={trans('generic.no_data.title', {
                        term: capitalizeAll(trans('label.time_series_data', { count: 2 })),
                    })}
                />
            ),
            emptyDataMessageComp: (
                <GenericMessageBox
                    title={trans('generic.no_data.title', {
                        term: capitalizeAll(trans('label.time_series_data', { count: 2 })),
                    })}
                />
            ),
        };
    }, [currentLanguage.locale2, filteredMetricsDataPoints, mergedMultiSeriesDataPoints?.length, onItemsPerPageChange, onNextPage, onPage, onPrevPage,
        searchParams.currentPage, searchParams.itemsPerPage, tableColumnsForSelectedMetrics, trans]);


    /**
     * Create Table view for the TS data
     */
    const createTabularView = useCallback(() => {
        return (
            <Grid item xs={12}>
                <TimeSeriesDataTable {...tableViewSettings()} />
            </Grid>
        );
    }, [tableViewSettings]);



    /**
     * When the user clicked on the number of data points from the timeline controller, we will show a dialog to allow the
     * user to enter a new value manually instead of using the controller.
     */
    const onTimelineScrollerPickInterval = useCallback((interval: number, errorMessage?: string) => {
        // Confirm before deleting an Entity. Because deleting an entity can be dangerous, we will ask the agent to enter the entity's name
        uiDispatch(showDialog({
            className     : classes.dataCountInputDialog,
            type          : DialogType.INPUT,
            title         : 'dialog.data_points_input.title',
            titleData     : { min: MIN_DATA_SIZE, max: MAX_DATA_SIZE },
            message       : 'dialog.data_points_input.message',
            messageData   : { min: MIN_DATA_SIZE, max: MAX_DATA_SIZE },
            positiveButton: 'label.ok',
            negativeButton: 'label.cancel',
            input         : {
                type        : 'number',
                name        : 'label.value',
                error       : errorMessage,
                required    : true,
                defaultValue: interval.toString(),

            },
            onClose: async (response?: DialogResponse): Promise<void> => {
                if (response && response.code === DialogResponseCode.POSITIVE) {
                    const newInterval = response.input ? Number(response.input.trim()) : undefined;

                    if (newInterval && newInterval >= MIN_DATA_SIZE && newInterval <= MAX_DATA_SIZE) {
                        onOtherFieldsChange(produce(searchParams.otherFields, (draft: Draft<DataSeriesSearchParams>) => {
                            draft.limit = newInterval;
                        }));
                    } else {
                        // Invalid interval value. Call this function again to show the dialog
                        onTimelineScrollerPickInterval(interval, 'error.input.generic.invalid');
                    }
                }
            },
        }));
    }, [classes.dataCountInputDialog, onOtherFieldsChange, searchParams.otherFields, uiDispatch]);



    const hasSelectedMetrics = searchParams.otherFields.selectedNumericMetrics.length > 0 || searchParams.otherFields.selectedTagMetrics.length > 0;
    const hasSelectedViews = timeSeriesViewOptions.showChartView || timeSeriesViewOptions.showTabularView;
    const dataCount = Math.max(props.multiSeriesNumericDataPoints.length, props.multiSeriesTagDataPoints.length);



    /**
     * Apply the user time, data count inputs and start fetching data for the provided input values.
     */
    const onApplyTimeInputs = useCallback(() => {
        if (timeSeriesViewOptions.aggregation === undefined && timestampInput) {
            // Get the startTime value from the 'startTimeInput' but make sure it is within the absolute start/end Date.
            // If it is out of bounds, fix the value
            const timestampValue = timestampInput.valueOf();
            const fixedTimestampValue = Math.max(props.absoluteStartDate, Math.min(props.absoluteEndDate, timestampValue));

            if (timeSeriesViewOptions.aggregation === undefined) {
                // We are in Raw data view mode so we only need to update these attributes
                onOtherFieldsChange(produce(searchParams.otherFields, (draft: Draft<DataSeriesSearchParams>) => {
                    draft.timestamp = fixedTimestampValue;
                    draft.limit = dataCountInput;
                    draft.beforeTimestamp = beforeTimestamp;
                }));

                // If we have fixed the start/end time, we need to update them so that the control bar show the fixed values
                if (fixedTimestampValue !== timestampValue) {
                    setTimestampInput(moment(fixedTimestampValue));
                }
            }
        } else if (timeSeriesViewOptions.aggregation !== undefined && startTimeInput && endTimeInput) {
            // Get the start and end time value from the 'startTimeInput' and 'endTimeInput' but make sure they are
            // within the absolute start/end Date AND endTime is greater than startTime. If they are out of bounds, fix the values
            const startTimeValue = startTimeInput.valueOf();
            const endTimeValue = endTimeInput.valueOf();

            const fixedStartTime = Math.max(props.absoluteStartDate, Math.min(props.absoluteEndDate, startTimeValue));
            const fixedEndTime = Math.max(fixedStartTime + Time.MINUTE_IN_MS, Math.min(props.absoluteEndDate, endTimeValue));

            // We are in Aggregated data view mode so we only need to update these attributes
            onOtherFieldsChange(produce(searchParams.otherFields, (draft: Draft<DataSeriesSearchParams>) => {
                draft.start = fixedStartTime;
                draft.end = fixedEndTime;
            }));

            // If we have fixed the start/end time, we need to update them so that the control bar show the fixed values
            if (fixedStartTime !== startTimeValue) {
                setStartTimeInput(moment(fixedStartTime));
            }
            if (fixedEndTime !== endTimeValue) {
                setEndTimeInput(moment(fixedEndTime));
            }
        }
    }, [beforeTimestamp, dataCountInput, endTimeInput, onOtherFieldsChange, props.absoluteEndDate, props.absoluteStartDate, searchParams.otherFields,
        startTimeInput, timeSeriesViewOptions.aggregation, timestampInput]);



    /**
     * Handler for the Previous/Next button click.
     */
    const onFetchPreviousOrNextDataSet = useCallback((isPrevious: boolean = true) => {
        const isViewingRawData = timeSeriesViewOptions.aggregation === undefined;

        // If we are in RAW metrics view, in order to fetch the NEXT/PREV set of data, we need to make sure we have at least 1 data point
        // from one of the Numeric or Tag metric. We need to use the data point's date as the starting date.
        if (isViewingRawData) {
            if ((beforeTimestamp && !isPrevious) || (!beforeTimestamp && isPrevious)) {
                // User was viewing data BEFORE the selected timestamp and now want to view NEXT set of data. Or the user was viewing
                // data AFTER the selected timestamp but now want to see PREVIOUS data. This mean the user wanted to change direction.
                // We will only change the beforeTimeStamp flag
                onOtherFieldsChange(produce(searchParams.otherFields, (draft: Draft<DataSeriesSearchParams>) => {
                    draft.beforeTimestamp = !beforeTimestamp;
                }));

                setBeforeTimestamp(!beforeTimestamp);
                return;
            }

            // If the user continue the same direction, we we move the selected timestamp backward or forward depending on
            // the beforeTimestamp flag.
            if (dataCount) {
                // Pick numerics data point array or tag metrics data point array which ever has data
                const dataPointArray = props.multiSeriesNumericDataPoints && props.multiSeriesNumericDataPoints.length > 0
                    ? props.multiSeriesNumericDataPoints : props.multiSeriesTagDataPoints;

                // Use the FIRST data point's timestamp as the new timestamp if we want to fetch PREVIOUS set of data
                // Use the LAST data point's timestamp as the new timestamp if we want to fetch NEXT set of data
                const newTimestamp = isPrevious ? dataPointArray[0].dateValue : dataPointArray[dataPointArray.length - 1].dateValue;

                onOtherFieldsChange(produce(searchParams.otherFields, (draft: Draft<DataSeriesSearchParams>) => {
                    draft.timestamp = newTimestamp;
                }));

                setTimestampInput(moment(newTimestamp));
            }
        } else if (!isViewingRawData) {
            // Find the interval before startTime and endTime and then create previous/next interval based on the start/end time
            // E.g. Start/end time = Jan 10 to Jan 15 therefore the interval is 5 days
            // So if we need to fetch PREVIOUS data set then we need to fetch data for Jan 5 to Jan 10
            // And if we need to fetch NEXT data set then we need to fetch data for Jan 15 to Jan 20
            const startTime = searchParams.otherFields.start;
            const endTime = searchParams.otherFields.end;
            const interval = endTime - startTime;
            const newStartTime = isPrevious ? startTime - interval : endTime;
            const newEndTime = isPrevious ? startTime : endTime + interval;

            onOtherFieldsChange(produce(searchParams.otherFields, (draft: Draft<DataSeriesSearchParams>) => {
                draft.start = newStartTime;
                draft.end = newEndTime;
            }));
            setStartTimeInput(moment(newStartTime));
            setEndTimeInput(moment(newEndTime));
        }
    }, [beforeTimestamp, dataCount, onOtherFieldsChange, props.multiSeriesNumericDataPoints, props.multiSeriesTagDataPoints,
        searchParams.otherFields, timeSeriesViewOptions.aggregation]);



    /**
     * On zoom button clicked, we will change the selected start/end time of the aggregated data. If `zoomIn` is true, we will shrink the
     * time interval by half around the center of the interval. If zoomIn is false (meaning zoom out), we will double the time interval
     * around the center of the interval. We will only do this for aggregated data view.
     */
    const onZoomButtonClick = useCallback((zoomIn: boolean) => {
        if (timeSeriesViewOptions.aggregation !== undefined) {
            const halfInterval = Math.floor((searchParams.otherFields.end - searchParams.otherFields.start) / 2);
            const quarterInterval = Math.floor((searchParams.otherFields.end - searchParams.otherFields.start) / 4);

            // When zoomIn, we will increase startTime by quarterInterval and decrease endTime by quarterInterval which is the same as
            // reducing the interval by half. When zoom out, we decrease startTime by halfInterval and increase endTime by halfInterval which
            // is the same as doubling the interval.
            const newStartTime = zoomIn ? searchParams.otherFields.start + quarterInterval : searchParams.otherFields.start - halfInterval;
            const newEndTime = zoomIn ? searchParams.otherFields.end - quarterInterval : searchParams.otherFields.end + halfInterval;

            onOtherFieldsChange(produce(searchParams.otherFields, (draft: Draft<DataSeriesSearchParams>) => {
                draft.start = newStartTime;
                draft.end = newEndTime;
            }));
            setStartTimeInput(moment(newStartTime));
            setEndTimeInput(moment(newEndTime));
        }
    }, [onOtherFieldsChange, searchParams.otherFields, timeSeriesViewOptions.aggregation]);



    /**
     * Create the Control Bar to control the data start, end, dataCount, etc...
     */
    const timeControlBar = useMemo(() => {
        const isViewingRawData = timeSeriesViewOptions.aggregation === undefined;

        // Because the order of the inputs for the RAW data is language dependence. The same order doesn't work well for all
        // languages. Therefore, we will use the translation system to define the order of the inputs.
        const inputsArrangement = (trans('ui.layout.data_time_control') ?? 'Data_Count_Input, Data_Count_Order_Input, Date_Input')
            .split(',').map((inputName) => {
                return inputName.trim();
            });

        const inputChanged = (isViewingRawData && (
            // If viewing RAW data, check these inputs
            timestampInput?.valueOf() !== searchParams.otherFields.timestamp
            || dataCountInput !== Math.abs(searchParams.otherFields.limit)
            || beforeTimestamp !== searchParams.otherFields.beforeTimestamp
        )) || (!isViewingRawData && (
            // If viewing AGGREGATED data, check these inputs
            startTimeInput?.valueOf() !== searchParams.otherFields.start
            || endTimeInput?.valueOf() !== searchParams.otherFields.end
        ));

        // Because we allow user to use Keyboard to set the start/end time therefore it is possible for the user to enter the wrong
        // date/time value. So we must make sure the start/end time value is valid before enabling the "Apply" button
        const isStartTimeValid = startTimeInput && !Number.isNaN(startTimeInput.valueOf());
        const isEndTimeValid = endTimeInput && !Number.isNaN(endTimeInput.valueOf());
        const isTimestampValid = timestampInput && !Number.isNaN(timestampInput.valueOf());

        const hasChangesToApply = Boolean(inputChanged);
        const canApplyChanges = Boolean((isViewingRawData && isTimestampValid) || (!isViewingRawData && isStartTimeValid && isEndTimeValid));

        // If in RAW data view, we can only enable the "Previous"/"Next" button if the user hasn't make changes to the input which requires
        // the user to Apply the changes first. Also we can only enable these 2 buttons if we have at least some data points for either
        // numeric metrics or tag metrics.
        // If in AGGREGATED data view, we can only enable these buttons if the user doesn't have any input to apply AND if we are not at the
        // very beginning or end of the data's absolute date
        const canEnablePreviousButton = isViewingRawData
            ? !hasChangesToApply && (!beforeTimestamp || dataCount > 1)
            : !hasChangesToApply && searchParams.otherFields.start > props.absoluteStartDate;

        const canEnableNextButton = isViewingRawData
            ? !hasChangesToApply && (beforeTimestamp || dataCount > 1)
            : !hasChangesToApply && searchParams.otherFields.end < props.absoluteEndDate;

        return (
            <div className={classes.timeControlBarContainer}>
                <Paper className={classes.timeControlBar} elevation={2}>
                    {isViewingRawData ? (
                        <>
                            {/* Input fields for RAW data */}
                            {inputsArrangement.map((inputName) => {
                                if (inputName === 'Data_Count_Input') {
                                    return (
                                        <TextField
                                            key={inputName}
                                            className="input data-count"
                                            type="number"
                                            label={capitalizeAll(trans('label.number_of_data_points'))}
                                            value={dataCountInput}
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            onChange={(event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
                                                setDataCountInput(Number(event.target.value));
                                            }}
                                            onBlur={() => {
                                                setDataCountInput((currentDataCount) => {
                                                    return Math.max(MIN_DATA_SIZE, Math.min(MAX_DATA_SIZE, Number(currentDataCount)));
                                                });
                                            }}
                                        />
                                    );
                                }
                                if (inputName === 'Data_Count_Order_Input') {
                                    return (
                                        <FormControl
                                            className="input data-count-order"
                                            key={inputName}
                                        >
                                            <InputLabel shrink>{capitalizeAll(trans(''))}</InputLabel>
                                            <Select
                                                value={beforeTimestamp}
                                                onChange={(event: React.ChangeEvent<{ value: unknown }>) => {
                                                // Select input's value is a string 'true' or 'false', not Boolean value so we need to convert
                                                // the string to Boolean
                                                    setBeforeTimestamp((event.target.value as string) === 'true');
                                                }}
                                            >
                                                <MenuItem value="true">{capitalizeAll(trans('label.before_timestamp'))}</MenuItem>
                                                <MenuItem value="false">{capitalizeAll(trans('label.after_timestamp'))}</MenuItem>
                                            </Select>
                                        </FormControl>
                                    );
                                }
                                if (inputName === 'Date_Input') {
                                    return (
                                        <KeyboardDateTimePicker
                                            key={inputName}
                                            className="input"
                                            fullWidth
                                            label={capitalizeAll(trans('label.date'))}
                                            allowKeyboardControl
                                            minDate={props.absoluteStartDate}
                                            maxDate={props.absoluteEndDate}
                                            value={timestampInput}
                                            format="YYYY/MM/DD HH:mm:ss"
                                            ampm={false}
                                            todayLabel={capitalizeFirst(trans('label.today'))}
                                            okLabel={capitalizeFirst(trans('label.ok'))}
                                            cancelLabel={capitalizeFirst(trans('label.cancel'))}
                                            invalidDateMessage={capitalizeFirst(trans('error.input.date.invalid'))}
                                            minDateMessage=""
                                            maxDateMessage=""
                                            showTodayButton
                                            disableFuture
                                            onChange={setTimestampInput}
                                        />
                                    );
                                }
                                return (<></>);
                            })}
                        </>
                    ) : (
                        <>
                            {/* Input fields for AGGREGATED data */}
                            <KeyboardDateTimePicker
                                className="input"
                                fullWidth
                                label={capitalizeAll(trans('label.start_date'))}
                                allowKeyboardControl
                                minDate={props.absoluteStartDate}
                                maxDate={endTimeInput}
                                value={startTimeInput}
                                format="YYYY/MM/DD HH:mm:ss"
                                ampm={false}
                                todayLabel={capitalizeFirst(trans('label.today'))}
                                okLabel={capitalizeFirst(trans('label.ok'))}
                                cancelLabel={capitalizeFirst(trans('label.cancel'))}
                                invalidDateMessage={capitalizeFirst(trans('error.input.date.invalid'))}
                                minDateMessage=""
                                maxDateMessage=""
                                showTodayButton
                                disableFuture
                                onChange={setStartTimeInput}
                            />
                            <KeyboardDateTimePicker
                                className="input"
                                fullWidth
                                label={capitalizeAll(trans('label.end_date'))}
                                allowKeyboardControl
                                minDate={startTimeInput}
                                maxDate={props.absoluteEndDate}
                                value={endTimeInput}
                                format="YYYY/MM/DD HH:mm:ss"
                                ampm={false}
                                todayLabel={capitalizeFirst(trans('label.today'))}
                                okLabel={capitalizeFirst(trans('label.ok'))}
                                cancelLabel={capitalizeFirst(trans('label.cancel'))}
                                invalidDateMessage={capitalizeFirst(trans('error.input.date.invalid'))}
                                minDateMessage=""
                                maxDateMessage=""
                                showTodayButton
                                disableFuture
                                onChange={setEndTimeInput}
                            />
                            {!hasChangesToApply && (
                                /*
                                    Show the data's resolution but only when we have fetched data. If the user changed the interval, we can't
                                    tell what the resolution until the user apply the change and backend returned the data and resolution of the data
                                 */
                                <TextField
                                    className="input resolution"
                                    label={capitalizeAll(trans('label.resolution'))}
                                    InputProps={{
                                        readOnly: true,
                                    }}
                                    value={trans(props.dataResolution ? getTimeSeriesResolutionName(props.dataResolution) : 'label.none')}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                />
                            )}
                        </>
                    )}
                    {hasChangesToApply && (
                        <Button
                            className="input"
                            variant="contained"
                            color="primary"
                            size="small"
                            disabled={!canApplyChanges}
                            onClick={onApplyTimeInputs}
                        >
                            {capitalizeAll(trans('label.apply'))}
                        </Button>
                    )}
                    {!hasChangesToApply && (
                        <>
                            <Button
                                className="input"
                                variant="contained"
                                color="primary"
                                size="small"
                                disabled={!canEnablePreviousButton}
                                onClick={() => {
                                    onFetchPreviousOrNextDataSet(true);
                                }}
                            >
                                {capitalizeAll(trans('label.prev'))}
                            </Button>
                            <Button
                                className="input"
                                variant="contained"
                                color="primary"
                                size="small"
                                disabled={!canEnableNextButton}
                                onClick={() => {
                                    onFetchPreviousOrNextDataSet(false);
                                }}
                            >
                                {capitalizeAll(trans('label.next'))}
                            </Button>
                            {timeSeriesViewOptions.aggregation !== undefined && (
                                <div
                                    className={clsx('input', 'zoom-buttons')}
                                >
                                    <IconButton
                                        size="medium"
                                        color="primary"
                                        // If backend return no resolution then that mean we have no more detailed data so we can disable zoom in
                                        disabled={props.dataResolution === undefined}
                                        onClick={() => {
                                            onZoomButtonClick(true);
                                        }}
                                    >
                                        <FontIcon iconName="fas fa-search-plus" />
                                    </IconButton>
                                    <IconButton
                                        size="medium"
                                        color="primary"
                                        onClick={() => {
                                            onZoomButtonClick(false);
                                        }}
                                    >
                                        <FontIcon iconName="fas fa-search-minus" />
                                    </IconButton>
                                </div>
                            )}
                        </>
                    )}
                </Paper>
            </div>
        );

    }, [timeSeriesViewOptions.aggregation, trans, timestampInput, searchParams.otherFields.timestamp, searchParams.otherFields.limit,
        searchParams.otherFields.beforeTimestamp, searchParams.otherFields.start, searchParams.otherFields.end, dataCountInput, beforeTimestamp,
        startTimeInput, endTimeInput, dataCount, props.absoluteStartDate, props.absoluteEndDate, props.dataResolution,
        classes.timeControlBarContainer, classes.timeControlBar, onApplyTimeInputs, onFetchPreviousOrNextDataSet, onZoomButtonClick]);



    return (
        <Box className={clsx(panelClasses.root, props.className, classes.root)}>

            {((props.numericMetrics && props.numericMetrics.length > 0) || (props.tagMetrics && props.tagMetrics.length > 0)) ? (
                <>
                    {/* Add a toolbar for the user to select metrics and start/end dates */}
                    {searchParams && (

                        <div className={clsx(classes.toolBar)}>
                            <div className={classes.toolBarTitle}>{capitalizeAll(trans('time_series_data.title'))}</div>

                            <Box display="flex" flexDirection="row" justifyContent="flex-end" flex={1} flexWrap="wrap">

                                <div className={classes.timeSeriesFilterMenu}>
                                    <Tooltip title={capitalizeFirst(trans('time_series_data.metrics_dropdown_tooltip'))}>
                                        <IconButton
                                            className={classes.timeSeriesFilterTogglerButton}
                                            onClick={onMetricsMenuAnchorClicked}
                                        >
                                            <Icon>visibility</Icon>
                                        </IconButton>
                                    </Tooltip>
                                    <Menu
                                        getContentAnchorEl={null}
                                        anchorEl={metricsMenuAnchorEl}
                                        keepMounted
                                        anchorOrigin={{
                                            vertical  : 'bottom',
                                            horizontal: 'right',
                                        }}
                                        transformOrigin={{
                                            vertical  : 'top',
                                            horizontal: 'right',
                                        }}
                                        open={isMetricsMenuOpened}
                                        onClose={onMetricsMenuClosed}
                                    >
                                        <MenuItem
                                            className={classes.timeSeriesFilterMenuItem}
                                            disabled={!props.numericMetrics || props.numericMetrics.length <= 0}
                                            selected={isNumericMetricsMenuOpened}
                                            onClick={onNumericMetricsMenuAnchorClicked}
                                        >
                                            <ListItemIcon className={classes.timeSeriesFilterMenuItemIcon}>
                                                <Icon>arrow_left</Icon>
                                            </ListItemIcon>
                                            {capitalizeAll(trans('label.numeric_metrics'))}
                                        </MenuItem>
                                        <MenuItem
                                            className={classes.timeSeriesFilterMenuItem}
                                            disabled={!props.tagMetrics || props.tagMetrics.length <= 0}
                                            selected={isTagMetricsMenuOpened}
                                            onClick={onTagMetricsMenuAnchorClicked}
                                        >
                                            <ListItemIcon className={classes.timeSeriesFilterMenuItemIcon}>
                                                <Icon>arrow_left</Icon>
                                            </ListItemIcon>
                                            {capitalizeAll(trans('label.tag_metrics'))}
                                        </MenuItem>
                                    </Menu>

                                    {props.numericMetrics && props.numericMetrics.length > 0 && (
                                        <Menu
                                            getContentAnchorEl={null}
                                            anchorEl={numericMetricsMenuAnchorEl}
                                            keepMounted
                                            anchorOrigin={{
                                                vertical  : 'top',
                                                horizontal: 'left',
                                            }}
                                            transformOrigin={{
                                                vertical  : 'top',
                                                horizontal: 'right',
                                            }}
                                            open={isNumericMetricsMenuOpened}
                                            onClose={onNumericMetricsMenuClosed}
                                        >
                                            <MenuItem
                                                className={classes.timeSeriesFilterMenuItem}
                                                onClick={onToggleSelectAllNumericMetrics}
                                            >
                                                <ListItemIcon className={classes.timeSeriesFilterMenuItemIcon}>
                                                    <Checkbox
                                                        color="primary"
                                                        checked={(!props.numericMetrics
                                                            || searchParams.otherFields.selectedNumericMetrics.length >= props.numericMetrics.length)}
                                                        size="small"
                                                    />
                                                </ListItemIcon>
                                                {
                                                    (props.numericMetrics
                                                    && searchParams.otherFields.selectedNumericMetrics.length < props.numericMetrics.length)
                                                        ? capitalizeFirst(trans('label.select_all'))
                                                        : capitalizeFirst(trans('label.unselect_all'))
                                                }
                                            </MenuItem>
                                            <Divider key="divider" />
                                            {props.numericMetrics.map((numericMetric: NumericMetricInfo): JSX.Element => {
                                                return (
                                                    <MenuItem
                                                        className={classes.timeSeriesFilterMenuItem}
                                                        key={numericMetric.name}
                                                        onClick={() => {
                                                            onToggleSelectedNumericMetric(numericMetric);
                                                        }}
                                                    >
                                                        <ListItemIcon className={classes.timeSeriesFilterMenuItemIcon}>
                                                            <Checkbox
                                                                color="primary"
                                                                checked={searchParams.otherFields.selectedNumericMetrics.find((
                                                                    metric: NumericMetricInfo,
                                                                ) => {
                                                                    return metric.name === numericMetric.name;
                                                                }) !== undefined}
                                                                size="small"
                                                            />
                                                        </ListItemIcon>
                                                        {numericMetric.displayName || numericMetric.name}
                                                    </MenuItem>
                                                );
                                            })}
                                        </Menu>
                                    )}


                                    {props.tagMetrics && props.tagMetrics.length > 0 && (
                                        <Menu
                                            getContentAnchorEl={null}
                                            anchorEl={tagMetricsMenuAnchorEl}
                                            keepMounted
                                            anchorOrigin={{
                                                vertical  : 'top',
                                                horizontal: 'left',
                                            }}
                                            transformOrigin={{
                                                vertical  : 'top',
                                                horizontal: 'right',
                                            }}
                                            open={isTagMetricsMenuOpened}
                                            onClose={onTagMetricsMenuClosed}
                                        >
                                            <MenuItem
                                                className={classes.timeSeriesFilterMenuItem}
                                                onClick={onToggleSelectAllTagMetrics}
                                            >
                                                <ListItemIcon className={classes.timeSeriesFilterMenuItemIcon}>
                                                    <Checkbox
                                                        color="primary"
                                                        checked={(!props.tagMetrics
                                                            || searchParams.otherFields.selectedTagMetrics.length >= props.tagMetrics.length)}
                                                        size="small"
                                                    />
                                                </ListItemIcon>
                                                {
                                                    (props.tagMetrics
                                                    && searchParams.otherFields.selectedTagMetrics.length < props.tagMetrics.length)
                                                        ? capitalizeFirst(trans('label.select_all'))
                                                        : capitalizeFirst(trans('label.unselect_all'))
                                                }
                                            </MenuItem>
                                            <Divider key="divider" />
                                            {props.tagMetrics && props.tagMetrics.map((tagMetric: TagMetricInfo): JSX.Element => {
                                                return (
                                                    <MenuItem
                                                        className={classes.timeSeriesFilterMenuItem}
                                                        key={tagMetric.name}
                                                        onClick={() => {
                                                            onToggleSelectedTagMetric(tagMetric);
                                                        }}
                                                    >
                                                        <ListItemIcon className={classes.timeSeriesFilterMenuItemIcon}>
                                                            <Checkbox
                                                                color="primary"
                                                                checked={searchParams.otherFields.selectedTagMetrics.find((
                                                                    metric: TagMetricInfo,
                                                                ) => {
                                                                    return metric.name === tagMetric.name;
                                                                }) !== undefined}
                                                                size="small"
                                                            />
                                                        </ListItemIcon>
                                                        {tagMetric.displayName || tagMetric.name}
                                                    </MenuItem>
                                                );
                                            })}
                                        </Menu>
                                    )}
                                </div>

                                <div className={classes.timeSeriesFilterMenu}>
                                    <IconButton
                                        className={classes.timeSeriesFilterTogglerButton}
                                        onClick={onViewOptionsMenuAnchorClicked}
                                    ><Icon>more_vert</Icon>
                                    </IconButton>
                                    <Menu
                                        getContentAnchorEl={null}
                                        anchorEl={viewOptionsMenuAnchorEl}
                                        keepMounted
                                        anchorOrigin={{
                                            vertical  : 'bottom',
                                            horizontal: 'right',
                                        }}
                                        transformOrigin={{
                                            vertical  : 'top',
                                            horizontal: 'right',
                                        }}
                                        open={isViewOptionsMenuOpened}
                                        onClose={onViewOptionsMenuClosed}
                                    >
                                        <MenuItem
                                            className={classes.timeSeriesFilterMenuItem}
                                            selected={isViewTypesMenuOpened}
                                            onClick={onViewTypesMenuAnchorClicked}
                                        >
                                            <ListItemIcon className={classes.timeSeriesFilterMenuItemIcon}>
                                                <Icon>arrow_left</Icon>
                                            </ListItemIcon>
                                            {capitalizeAll(trans('label.show_view_type'))}
                                        </MenuItem>
                                        <MenuItem
                                            className={classes.timeSeriesFilterMenuItem}
                                            selected={isAggrTypesMenuOpened}
                                            onClick={onAggrTypesMenuAnchorClicked}
                                        >
                                            <ListItemIcon className={classes.timeSeriesFilterMenuItemIcon}>
                                                <Icon>arrow_left</Icon>
                                            </ListItemIcon>
                                            {capitalizeAll(trans('label.time_series_aggregation_type'))}
                                        </MenuItem>
                                        <MenuItem
                                            className={classes.timeSeriesFilterMenuItem}
                                            selected={isChartOptionsMenuOpened}
                                            disabled={!timeSeriesViewOptions.showChartView}
                                            onClick={onChartOptionsMenuAnchorClicked}
                                        >
                                            <ListItemIcon className={classes.timeSeriesFilterMenuItemIcon}>
                                                <Icon>arrow_left</Icon>
                                            </ListItemIcon>
                                            {capitalizeAll(trans('label.chart_options'))}
                                        </MenuItem>
                                    </Menu>


                                    {/* Menu for selecting View type e.g. Charts/Tabular */}
                                    <Menu
                                        getContentAnchorEl={null}
                                        anchorEl={viewTypesMenuAnchorEl}
                                        keepMounted
                                        anchorOrigin={{
                                            vertical  : 'top',
                                            horizontal: 'left',
                                        }}
                                        transformOrigin={{
                                            vertical  : 'top',
                                            horizontal: 'right',
                                        }}
                                        open={isViewTypesMenuOpened}
                                        onClose={onViewTypesMenuClosed}
                                    >
                                        <MenuItem
                                            className={classes.timeSeriesFilterMenuItem}
                                            onClick={onToggleShowChartView}
                                        >
                                            <ListItemIcon className={classes.timeSeriesFilterMenuItemIcon}>
                                                <Checkbox
                                                    color="primary"
                                                    checked={timeSeriesViewOptions.showChartView}
                                                    size="small"
                                                />
                                            </ListItemIcon>
                                            {capitalizeAll(trans('label.show_chart_view'))}
                                        </MenuItem>
                                        <MenuItem
                                            className={classes.timeSeriesFilterMenuItem}
                                            onClick={onToggleShowTabularView}
                                        >
                                            <ListItemIcon className={classes.timeSeriesFilterMenuItemIcon}>
                                                <Checkbox
                                                    color="primary"
                                                    checked={timeSeriesViewOptions.showTabularView}
                                                    size="small"
                                                />
                                            </ListItemIcon>
                                            {capitalizeAll(trans('label.show_tabular_view'))}
                                        </MenuItem>
                                    </Menu>


                                    {/* Menu for selecting Aggregation type */}
                                    <Menu
                                        getContentAnchorEl={null}
                                        anchorEl={aggrTypesMenuAnchorEl}
                                        keepMounted
                                        anchorOrigin={{
                                            vertical  : 'top',
                                            horizontal: 'left',
                                        }}
                                        transformOrigin={{
                                            vertical  : 'top',
                                            horizontal: 'right',
                                        }}
                                        open={isAggrTypesMenuOpened}
                                        onClose={onAggrTypesMenuClosed}
                                    >
                                        <RadioGroup value={timeSeriesViewOptions.aggregation || ''}>
                                            {Object.values(ExtTimeSeriesAggregation).map((aggregation) => {
                                                return (
                                                    <MenuItem
                                                        key={aggregation}
                                                        className={clsx(classes.timeSeriesFilterMenuItem)}
                                                        onClick={() => {
                                                            onSetAggregationType(aggregation);
                                                        }}
                                                    >
                                                        <ListItemIcon className={classes.timeSeriesFilterMenuItemIcon}>
                                                            <Radio
                                                                color="primary"
                                                                size="small"
                                                                value={aggregation}
                                                            />
                                                        </ListItemIcon>
                                                        {capitalizeAll(trans(getExtTimeSeriesAggregationTypeName(aggregation)))}
                                                    </MenuItem>
                                                );
                                            })}
                                            <MenuItem
                                                className={clsx(classes.timeSeriesFilterMenuItem)}
                                                onClick={() => {
                                                    onSetAggregationType(undefined);
                                                }}
                                            >
                                                <ListItemIcon className={classes.timeSeriesFilterMenuItemIcon}>
                                                    <Radio
                                                        color="primary"
                                                        size="small"
                                                        value=""
                                                    />
                                                </ListItemIcon>
                                                {capitalizeAll(trans('label.none'))}
                                            </MenuItem>
                                        </RadioGroup>
                                    </Menu>



                                    {/* Menu for selecting Chart options which is only enabled if view type Chart is turned on */}
                                    {timeSeriesViewOptions.showChartView && (
                                        <Menu
                                            getContentAnchorEl={null}
                                            anchorEl={chartOptionsMenuAnchorEl}
                                            keepMounted
                                            anchorOrigin={{
                                                vertical  : 'top',
                                                horizontal: 'left',
                                            }}
                                            transformOrigin={{
                                                vertical  : 'top',
                                                horizontal: 'right',
                                            }}
                                            open={isChartOptionsMenuOpened}
                                            onClose={onChartOptionsMenuClosed}
                                        >
                                            <MenuItem
                                                className={classes.timeSeriesFilterMenuItem}
                                                onClick={onToggleCombineSeries}
                                            >
                                                <ListItemIcon className={classes.timeSeriesFilterMenuItemIcon}>
                                                    <Checkbox
                                                        color="primary"
                                                        checked={timeSeriesViewOptions.combineSeries}
                                                        size="small"
                                                    />
                                                </ListItemIcon>
                                                {capitalizeAll(trans('label.combine_series'))}
                                            </MenuItem>

                                            <MenuItem
                                                className={classes.timeSeriesFilterMenuItem}
                                                onClick={onToggleShowChartBullets}
                                            >
                                                <ListItemIcon className={classes.timeSeriesFilterMenuItemIcon}>
                                                    <Checkbox
                                                        color="primary"
                                                        checked={timeSeriesViewOptions.showChartBullets}
                                                        size="small"
                                                    />
                                                </ListItemIcon>
                                                {capitalizeAll(trans('label.show_chart_bullets'))}
                                            </MenuItem>
                                            <Divider />

                                            {/* Enable chart Size options if user unselected 'Combine Series' */}
                                            <RadioGroup value={timeSeriesViewOptions.chartSize}>
                                                {Object.values(ChartSize).map((size: ChartSize) => {
                                                    return (
                                                        <MenuItem
                                                            key={size}
                                                            disabled={timeSeriesViewOptions.combineSeries}
                                                            className={clsx(classes.timeSeriesFilterMenuItem)}
                                                            onClick={() => {
                                                                onSetChartSize(size);
                                                            }}
                                                        >
                                                            <ListItemIcon className={classes.timeSeriesFilterMenuItemIcon}>
                                                                <Radio
                                                                    color="primary"
                                                                    size="small"
                                                                    value={size}
                                                                />
                                                            </ListItemIcon>
                                                            {capitalizeAll(trans(`label.chart_size_${size}`))}
                                                        </MenuItem>
                                                    );
                                                })}
                                            </RadioGroup>
                                        </Menu>
                                    )}
                                </div>
                            </Box>
                        </div>
                    )}



                    {/* Add Charts and Table to show time series data */}
                    <Grid container spacing={3}>
                        {dataCount > 0 && hasSelectedMetrics && hasSelectedViews && (
                            <>
                                {/** Create display for each view type. If no viewTypes is specified, display Chart as default. * */}
                                {timeSeriesViewOptions.showChartView && (
                                    createChartViews()
                                )}
                                {timeSeriesViewOptions.showTabularView && (
                                    createTabularView()
                                )}
                            </>
                        )}

                        {/*
                            Show empty search result if there is no data
                        */}
                        {(hasSelectedMetrics && hasSelectedViews && !dataCount) && (
                            <Box marginTop={3} flex={1}>
                                <GenericMessageBox
                                    title={trans('generic.no_data.title', {
                                        term: capitalizeAll(trans('label.time_series_data', { count: 2 })),
                                    })}
                                />
                            </Box>
                        )}

                        {/*
                            Show empty list result if the user didn't select and metric user turned off both chart and table view
                        */}
                        {(!hasSelectedMetrics || !hasSelectedViews) && (
                            <Box marginTop={3} flex={1}>
                                <GenericMessageBox
                                    title={trans('generic.no_data_selected.title', {
                                        term: capitalizeAll(trans('label.view', { count: 2 })),
                                    })}
                                />
                            </Box>
                        )}
                    </Grid>


                    {hasSelectedViews && hasSelectedMetrics && timeControlBar}
                </>
            ) : (
                <Box marginTop={3} flex={1}>
                    <GenericMessageBox
                        title={trans('generic.no_data.title', {
                            term: capitalizeAll(trans('label.time_series_data', { count: 2 })),
                        })}
                    />
                </Box>
            )}
        </Box>
    );
};
